import React from "react"

import { Psychologist } from "utils/interfaces"
import { PsychologistDiscountBadge } from "components/PsychologistDiscountBadge"
import { Analytics } from "utils/analytics"
import { ProfileLink } from "app/AppRouter/links"
import { useClientContext } from "modules/ClientAuthorization/common/ClientContext"
import { useMaxExperience, useReportPsychologistNameCopy } from "utils/hooks"
import { AudioMark } from "modules/SelectionOfPsychologists/components/Card/AudioMark"
import { PsychologistAvatar } from "components/PsychologistAvatar/PsychologistAvatar"
import { getLocationPath } from "utils/helpers"
import { PsychologistTitles } from "modules/PsyCommon/components"
import { FriendlyBar } from "modules/PsyCatalog/common/FriendlyBar"
import { CompetencesSection } from "modules/PsyCatalog/common/CompetencesSection"
import { PsychologistPrice } from "modules/PsyCatalog/common/PsychologistPrice"
import {
    formatExperience,
    onlineSessionAvailable,
    renderCityWithDetails,
} from "utils/helpers/psychologistUtil"
import { APP_STATUSES } from "utils/AppStatusContext"
import { isBrowser } from "utils/infrastructure/ssg-utils"
import { useShowPrice } from "utils/hooks/useShowPrice"

interface Props {
    psy: Psychologist
    position?: string
    resultsKey?: string
    showFriendly?: boolean
}

export function PsyCard({ psy, resultsKey, position }: Props) {
    const [{ isVerified }] = useClientContext()
    const showPrice = useShowPrice()
    const { experience } = useMaxExperience(psy)

    useReportPsychologistNameCopy(psy.display_name, "catalog_card")

    const pathName = isBrowser()
        ? location.href.includes("manual=1")
            ? "/psychologists"
            : getLocationPath()
        : undefined

    return (
        <div className="psychologist-list__psychologist psychologist-card">
            <ProfileLink
                profileUrl={psy.profile_url}
                resultsKey={resultsKey}
                position={position}
                target="_blank"
                from={APP_STATUSES.search_page}
                fromLink={pathName}
                className="psychologist"
            >
                <PsychologistAvatar
                    psy={psy}
                    size="L"
                    className="psychologist__avatar"
                    renderAfter={psy.audio || psy.video ? <AudioMark /> : null}
                />

                {!isVerified && <PsychologistDiscountBadge psy={psy} />}
                <h2 className="text-center">
                    <div className="psychologist__title">{psy.display_name}</div>
                    <PsychologistTitles psy={psy} className="psychologist__subtitle" />
                </h2>
                <FriendlyBar
                    fem={psy.working_with.adults.fem}
                    friendly={psy.working_with.adults.friendly}
                    believer={psy.working_with.adults.believer}
                    cancer={psy.working_with.adults.cancer}
                />
                <ul className="benefits__list">
                    <li className="benefits__list-item">
                        <div>
                            <p className="benefits__list-item-text benefits__list-item-text--title">
                                Опыт{" "}
                            </p>
                            <p className="benefits__list-item-text benefits__list-item-text--subtitle">
                                {formatExperience(experience)}
                            </p>
                        </div>
                    </li>
                    {showPrice && (
                        <li className="benefits__list-item">
                            <div>
                                <p className="benefits__list-item-text benefits__list-item-text--title">
                                    Стоимость
                                </p>
                                <p className="benefits__list-item-text benefits__list-item-text--price">
                                    <PsychologistPrice
                                        priceClassName=""
                                        discountClassName="profile__psychologist-discount__price-with-discount-gray"
                                        psy={psy}
                                    />
                                </p>
                            </div>
                        </li>
                    )}
                    <li className="benefits__list-item">
                        <div>
                            <p className="benefits__list-item-text benefits__list-item-text--title">
                                Видеосвязь
                            </p>
                            <p className="benefits__list-item-text benefits__list-item-text--subtitle">
                                {onlineSessionAvailable(psy) ? "Да" : "Нет"}
                            </p>
                        </div>
                    </li>
                </ul>
                <div className="psychologist__details-container">
                    <div className="psychologist__details">
                        <div className="psychologist__details-title">Где ведет прием</div>
                        <ul className="psychologist__details-list details__list details__list--without-style">
                            <li className="details__list-item">
                                {renderCityWithDetails(psy.new_city.name, psy.city_details)}
                            </li>
                        </ul>
                    </div>
                    <div className="psychologist__details">
                        <div className="psychologist__details-title">С чем работает</div>
                        <CompetencesSection
                            competences={psy.competences.map((item) => item.short_name)}
                        />
                    </div>
                </div>
            </ProfileLink>
            <div className="is-flex is-flex-direction-column psychologist__buttons-wrapper">
                <ProfileLink
                    profileUrl={psy.profile_url}
                    resultsKey={resultsKey}
                    position={position}
                    onClick={() => {
                        Analytics.to_profile_clicked()
                    }}
                    target="_blank"
                    from={APP_STATUSES.search_page}
                    fromLink={pathName}
                    className="button-primary-rounded buttons-on-card"
                >
                    Подробнее
                </ProfileLink>
            </div>
        </div>
    )
}
